import {alpha, type Theme} from '@mui/material/styles';

export const FIELD_HEIGHT = 51;
export const FIELD_RADIUS = 100;
export const FIELD_PADDING_X = 20;
export const MENU_RADIUS = 4;
const MENU_SHADOW_ALPHA = 0.05;
export const menuShadow = (theme: Theme) => {
  const tint = alpha(theme.palette.colors.base.black, MENU_SHADOW_ALPHA);
  return [`0px 10px 15px -3px ${tint}`, `0px 4px 6px -4px ${tint}`].join(', ');
};

interface ShellState {
  error?: boolean;
  disabled?: boolean;
}

// Standard `:-webkit-autofill` override: browsers otherwise paint autofilled fields
// with their own baby-blue/yellow background via an internal box-shadow, ignoring our
// `background-color`. The inset box-shadow trick repaints that layer with the field's
// own white fill and text color instead of disabling autofill styling entirely.
export function autofillOverride(theme: Theme) {
  const colors = theme.palette.colors;
  return {
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
      boxShadow: `0 0 0 1000px ${colors.surface.neutral.default} inset`,
      WebkitTextFillColor: colors.text.neutral.strong,
      caretColor: colors.text.neutral.strong,
    },
  } as const;
}

export function fieldShellStyles(theme: Theme, {error, disabled}: ShellState = {}) {
  const colors = theme.palette.colors;
  const idleBorder = disabled ? colors.border.neutral.disabled : colors.border.neutral.subtle;
  const focusBorder = colors.border.focus.default;
  return {
    minHeight: FIELD_HEIGHT,
    width: '100%',
    boxSizing: 'border-box' as const,
    paddingInline: FIELD_PADDING_X,
    borderRadius: theme.radii.full,
    backgroundColor: colors.surface.neutral.default,
    border: `1px solid ${error ? colors.border.error.default : idleBorder}`,
    color: colors.text.neutral.strong,
    boxShadow: 'none',
    transition: theme.transitions.create(['border-color']),
    '&:hover': {
      borderColor: error ? colors.border.error.default : colors.border.neutral.hover,
    },
    '&.Mui-focused, &:focus-within': {
      borderColor: error ? colors.border.error.default : focusBorder,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.surface.neutral.disabled,
      borderColor: colors.border.neutral.disabled,
    },
    '& input, & textarea': {
      ...autofillOverride(theme),
    },
  } as const;
}
